import { Check, Copy } from 'lucide-react';
import { useEffect, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';

/**
 * Copy a value to the clipboard, with confirmation.
 *
 * Small, but load-bearing on the deposit screen: the depositor is about to type an account number
 * into their banking app, and a transposed digit sends real money to the wrong person. Copying is
 * the safe path, so it is the prominent one — and the confirmation is explicit, because a silent
 * copy leaves the user unsure whether to retype it anyway.
 */
export function CopyButton({
  value,
  label,
  size = 'icon',
  className,
}: {
  /** Text placed on the clipboard. */
  value: string;
  /** Accessible description of what is being copied. */
  label: string;
  /** Button size. */
  size?: 'icon' | 'sm' | undefined;
  /** Additional classes. */
  className?: string | undefined;
}): ReactNode {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) return;
    const id = setTimeout(() => {
      setCopied(false);
    }, 1_800);
    return () => {
      clearTimeout(id);
    };
  }, [copied]);

  return (
    <Button
      type="button"
      variant="ghost"
      size={size}
      className={className}
      aria-label={
        copied
          ? t('domain.copyButton.copiedAriaLabel', { label })
          : t('domain.copyButton.copyAriaLabel', { label })
      }
      onClick={() => {
        void navigator.clipboard
          ?.writeText(value)
          .then(() => {
            setCopied(true);
          })
          .catch(() => {
            setCopied(false);
          });
      }}
    >
      {copied ? (
        <Check className="text-state-good" aria-hidden="true" />
      ) : (
        <Copy aria-hidden="true" />
      )}
      {size === 'sm' ? (
        <span>{copied ? t('domain.copyButton.copied') : t('domain.copyButton.copy')}</span>
      ) : null}
      <span aria-live="polite" className="sr-only">
        {copied ? t('domain.copyButton.copiedToClipboard', { label }) : ''}
      </span>
    </Button>
  );
}
